// InnerCirclePanel.jsx - Protocol 11 & 12: Strategic Specialists for Hub Bid Intel
// One-time contractors who provide intel over 30 days leading to hub bid deadline

import React from 'react';
import { Star, AlertTriangle, Shield, Skull, Eye, User, TrendingUp, Megaphone, Wrench } from 'lucide-react';

// =============================================================================
// SPECIALIST ROSTER - Per Protocol 12.3
// =============================================================================

const SPECIALIST_ROSTER = [
  // CLEAN - No Risk
  {
    id: 'accountant-2star',
    name: 'Harold Chen',
    title: 'Master Accountant',
    category: 'clean',
    fee: 500000,
    stars: 2,
    reliability: [40, 50],
    risk: 'none',
    icon: TrendingUp,
    description: 'Analyzes competitor financial filings and public records.',
    intelType: 'Financial estimates based on public data',
  },
  {
    id: 'accountant-4star',
    name: 'Victoria Ashford',
    title: 'Master Accountant',
    category: 'clean',
    fee: 1500000,
    stars: 4,
    reliability: [75, 85],
    risk: 'none',
    icon: TrendingUp,
    description: 'Former Big Four partner with deep industry connections.',
    intelType: 'Precise financial intelligence from trusted sources',
  },
  {
    id: 'pr-2star',
    name: 'Derek Simmons',
    title: 'Master PR',
    category: 'clean',
    fee: 750000,
    stars: 2,
    reliability: [40, 50],
    risk: 'none',
    icon: Megaphone,
    description: 'Media analyst tracking competitor announcements and sentiment.',
    intelType: 'Public perception and media strategy analysis',
  },
  {
    id: 'pr-4star',
    name: 'Isabella Cruz',
    title: 'Master PR',
    category: 'clean',
    fee: 2000000,
    stars: 4,
    reliability: [75, 85],
    risk: 'none',
    icon: Megaphone,
    description: 'Former airline communications VP with executive contacts.',
    intelType: 'Insider knowledge of competitor PR strategies',
  },
  {
    id: 'analyst-2star',
    name: 'Raymond Park',
    title: 'Master Analyst',
    category: 'clean',
    fee: 1000000,
    stars: 2,
    reliability: [45, 55],
    risk: 'none',
    icon: Eye,
    description: 'Aviation industry researcher with access to proprietary databases.',
    intelType: 'Market analysis and competitive positioning',
  },
  {
    id: 'analyst-5star',
    name: 'Dr. Evelyn Hartwell',
    title: 'Master Analyst',
    category: 'clean',
    fee: 3000000,
    stars: 5,
    reliability: [90, 95],
    risk: 'none',
    icon: Eye,
    description: 'Former DOT economist. Legendary accuracy in bid predictions.',
    intelType: 'Near-certain competitive intelligence',
  },

  // GRAY - Medium Risk
  {
    id: 'fixer-2star',
    name: 'Tony Marchetti',
    title: 'Master Fixer',
    category: 'gray',
    fee: 2000000,
    stars: 2,
    reliability: [45, 55],
    risk: 'medium',
    icon: Wrench,
    description: 'Knows people who know people. Works the margins.',
    intelType: 'Unofficial channels and rumor networks',
  },
  {
    id: 'fixer-5star',
    name: 'Natasha Volkov',
    title: 'Master Fixer',
    category: 'gray',
    fee: 6000000,
    stars: 5,
    reliability: [90, 95],
    risk: 'medium',
    icon: Wrench,
    description: 'Legendary connector. If information exists, she finds it.',
    intelType: 'Comprehensive intelligence from any source',
  },

  // DARK - High Risk
  {
    id: 'insider-3star',
    name: 'Marcus Webb',
    title: 'Master Insider',
    category: 'dark',
    fee: 4000000,
    stars: 3,
    reliability: [55, 65],
    risk: 'high',
    icon: User,
    description: 'Has contacts inside competitor organizations.',
    intelType: 'Direct intel from competitor employees',
  },
  {
    id: 'insider-5star',
    name: 'Diana Reyes',
    title: 'Master Insider',
    category: 'dark',
    fee: 12000000,
    stars: 5,
    reliability: [92, 98],
    risk: 'high',
    icon: User,
    description: 'Former airline executive. Still has the keys to the kingdom.',
    intelType: 'Board-level intelligence with extreme accuracy',
  },

  // EXTREME Risk
  {
    id: 'saboteur-3star',
    name: 'Ghost',
    title: 'Master Saboteur',
    category: 'extreme',
    fee: 8000000,
    stars: 3,
    reliability: [60, 70],
    risk: 'extreme',
    icon: Skull,
    description: 'Specializes in disrupting competitor operations.',
    intelType: 'Active interference + intelligence gathering',
  },
  {
    id: 'saboteur-5star',
    name: 'The Architect',
    title: 'Master Saboteur',
    category: 'extreme',
    fee: 20000000,
    stars: 5,
    reliability: [95, 99],
    risk: 'extreme',
    icon: Skull,
    description: 'Legendary operative. No one knows their real identity.',
    intelType: 'Complete competitor disruption and total intelligence',
  },
];

// =============================================================================
// RISK BADGE CONFIG
// =============================================================================

const RISK_CONFIG = {
  none: { color: '#22c55e', bg: '#22c55e20', label: 'CLEAN', icon: Shield },
  medium: { color: '#f59e0b', bg: '#f59e0b20', label: 'MEDIUM RISK', icon: AlertTriangle },
  high: { color: '#ef4444', bg: '#ef444420', label: 'HIGH RISK', icon: AlertTriangle },
  extreme: { color: '#dc2626', bg: '#dc262620', label: 'EXTREME', icon: Skull },
};

// =============================================================================
// FORMAT HELPERS
// =============================================================================

function formatMoney(amount) {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  return `$${(amount / 1000).toFixed(0)}K`;
}

function renderStars(count) {
  return (
    <div style={{ display: 'flex', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          fill={n <= count ? '#fbbf24' : 'transparent'}
          color={n <= count ? '#fbbf24' : '#525252'}
        />
      ))}
    </div>
  );
}

// =============================================================================
// SPECIALIST CARD COMPONENT
// =============================================================================

function SpecialistCard({ specialist, cash, isHired, onHire }) {
  const riskConfig = RISK_CONFIG[specialist.risk];
  const RiskIcon = riskConfig.icon;
  const SpecIcon = specialist.icon;
  const canAfford = cash >= specialist.fee;
  const disabled = !canAfford || isHired;

  return (
    <div
      style={{
        backgroundColor: '#111',
        border: `1px solid ${isHired ? '#22c55e' : '#1a1a1a'}`,
        borderRadius: '8px',
        padding: '16px',
        opacity: isHired ? 0.6 : 1,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: `${riskConfig.bg}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SpecIcon size={20} color={riskConfig.color} />
          </div>
          <div>
            <div style={{ color: '#e5e5e5', fontWeight: 'bold', fontSize: '14px' }}>{specialist.name}</div>
            <div style={{ color: '#737373', fontSize: '12px' }}>{specialist.title}</div>
          </div>
        </div>
        {renderStars(specialist.stars)}
      </div>

      {/* Description */}
      <p style={{ color: '#a3a3a3', fontSize: '12px', marginBottom: '12px', lineHeight: '1.4' }}>
        {specialist.description}
      </p>

      {/* Reliability */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ color: '#525252', fontSize: '11px' }}>RELIABILITY:</span>
        <span style={{ color: '#60ffd0', fontSize: '12px', fontWeight: 'bold' }}>
          {specialist.reliability[0]}-{specialist.reliability[1]}%
        </span>
      </div>

      {/* Risk Badge */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: riskConfig.bg,
          border: `1px solid ${riskConfig.color}`,
          marginBottom: '16px',
        }}
      >
        <RiskIcon size={12} color={riskConfig.color} />
        <span style={{ color: riskConfig.color, fontSize: '10px', fontWeight: 'bold' }}>{riskConfig.label}</span>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '16px' }}>{formatMoney(specialist.fee)}</div>
          <div style={{ color: '#525252', fontSize: '10px' }}>ONE-TIME FEE</div>
        </div>
        <button
          onClick={() => onHire(specialist)}
          disabled={disabled}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            fontWeight: 'bold',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: isHired ? '#22c55e20' : canAfford ? '#60ffd0' : '#333',
            color: isHired ? '#22c55e' : canAfford ? '#000' : '#525252',
          }}
        >
          {isHired ? 'HIRED' : canAfford ? 'HIRE' : 'CANT AFFORD'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PANEL COMPONENT
// =============================================================================

export function InnerCirclePanel({ innerCircleState, cash, onHire, onClose }) {
  const hired = innerCircleState?.hired || [];
  const hiredIds = new Set(hired.map((s) => s.id));

  // Group specialists by category
  const cleanSpecialists = SPECIALIST_ROSTER.filter((s) => s.category === 'clean');
  const graySpecialists = SPECIALIST_ROSTER.filter((s) => s.category === 'gray');
  const darkSpecialists = SPECIALIST_ROSTER.filter((s) => s.category === 'dark' || s.category === 'extreme');

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>THE INNER CIRCLE</div>
            <div style={styles.headerSubtitle}>Strategic Specialists for Hub Bid Intelligence</div>
          </div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* Cash Display */}
        <div style={styles.cashBar}>
          <span style={{ color: '#737373' }}>Available Funds:</span>
          <span style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '18px' }}>
            ${(cash / 1000000).toFixed(1)}M
          </span>
        </div>

        {/* Hired Summary */}
        {hired.length > 0 && (
          <div style={styles.hiredSummary}>
            <div style={{ color: '#60ffd0', fontWeight: 'bold', marginBottom: '8px' }}>
              ACTIVE CONTRACTS ({hired.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {hired.map((s) => (
                <div
                  key={s.id}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #22c55e',
                    fontSize: '12px',
                    color: '#e5e5e5',
                  }}
                >
                  {s.name} ({s.title})
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div style={styles.content}>
          {/* CLEAN Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Shield size={16} color="#22c55e" />
              <span>CLEAN OPERATIONS</span>
              <span style={styles.sectionSubtext}>Legal research, no risk</span>
            </div>
            <div style={styles.grid}>
              {cleanSpecialists.map((spec) => (
                <SpecialistCard
                  key={spec.id}
                  specialist={spec}
                  cash={cash}
                  isHired={hiredIds.has(spec.id)}
                  onHire={onHire}
                />
              ))}
            </div>
          </div>

          {/* GRAY Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <AlertTriangle size={16} color="#f59e0b" />
              <span>GRAY OPERATIONS</span>
              <span style={styles.sectionSubtext}>Semi-legal, medium risk</span>
            </div>
            <div style={styles.grid}>
              {graySpecialists.map((spec) => (
                <SpecialistCard
                  key={spec.id}
                  specialist={spec}
                  cash={cash}
                  isHired={hiredIds.has(spec.id)}
                  onHire={onHire}
                />
              ))}
            </div>
          </div>

          {/* DARK Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Skull size={16} color="#ef4444" />
              <span>DARK OPERATIONS</span>
              <span style={styles.sectionSubtext}>Illegal, high/extreme risk</span>
            </div>
            <div style={styles.warningBanner}>
              <AlertTriangle size={14} />
              <span>These specialists may get caught. Consequences include fines, reputation loss, FAA investigation, or game over.</span>
            </div>
            <div style={styles.grid}>
              {darkSpecialists.map((spec) => (
                <SpecialistCard
                  key={spec.id}
                  specialist={spec}
                  cash={cash}
                  isHired={hiredIds.has(spec.id)}
                  onHire={onHire}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    backgroundColor: '#0a0a0a',
    border: '1px solid #1a1a1a',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #1a1a1a',
    backgroundColor: '#0c0c0c',
  },
  headerTitle: {
    color: '#60ffd0',
    fontSize: '24px',
    fontWeight: 'bold',
  },
  headerSubtitle: {
    color: '#737373',
    fontSize: '12px',
    marginTop: '4px',
  },
  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#1a1a1a',
    color: '#737373',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cashBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 24px',
    backgroundColor: '#0c0c0c',
    borderBottom: '1px solid #1a1a1a',
  },
  hiredSummary: {
    padding: '16px 24px',
    backgroundColor: '#0d1a14',
    borderBottom: '1px solid #1a1a1a',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  section: {
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#e5e5e5',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '16px',
    textTransform: 'uppercase',
  },
  sectionSubtext: {
    color: '#525252',
    fontSize: '11px',
    fontWeight: 'normal',
    marginLeft: '8px',
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#1a0a0a',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    marginBottom: '16px',
    color: '#ef4444',
    fontSize: '12px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
};

export default InnerCirclePanel;
